import React from "react";
import type { DeviceFormData, SSHConfig, SSHCommand } from "../lib/device";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Plus, X } from "lucide-react";

interface AddSSHProps {
  formData: DeviceFormData;
  setFormData: React.Dispatch<React.SetStateAction<DeviceFormData>>;
  errors: Partial<Record<string, string>>;
}

function AddSSH({ formData, setFormData, errors }: AddSSHProps) {
  // For now, support only one SSHConfig (can be extended for multiple hosts later)
  const sshConfig = formData.ssh && formData.ssh.length > 0 ? formData.ssh[0] : {
    host: "",
    port: 22,
    username: "",
    enc_password: "",
    private_key: "",
    commands: [{ name: "", command: "" }],
  };

  // Update SSH config fields
  const handleConfigChange = (field: keyof SSHConfig, value: any) => {
    const updatedConfig = { ...sshConfig, [field]: value };
    setFormData((prev) => ({ ...prev, ssh: [updatedConfig] }));
  };

  // Update a command in the commands array
  const handleCommandChange = (idx: number, field: keyof SSHCommand, value: string) => {
    const updatedCommands = sshConfig.commands.map((cmd, i) =>
      i === idx ? { ...cmd, [field]: value } : cmd
    );
    handleConfigChange("commands", updatedCommands);
  };

  // Add a new command
  const addCommand = () => {
    handleConfigChange("commands", [...sshConfig.commands, { name: "", command: "" }]);
  };

  // Remove a command
  const removeCommand = (idx: number) => {
    const updatedCommands = sshConfig.commands.filter((_, i) => i !== idx);
    handleConfigChange("commands", updatedCommands);
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">SSH Configuration</h2>
      <div className="space-y-4">
        <div>
          <Label htmlFor="sshHost">Host</Label>
          <Input
            id="sshHost"
            value={sshConfig.host}
            onChange={(e) => handleConfigChange("host", e.target.value)}
            placeholder="e.g., 192.168.1.10"
            className={errors.sshHost ? "border-red-500" : ""}
          />
          {errors.sshHost && <p className="text-sm text-red-500">{errors.sshHost}</p>}
        </div>
        <div>
          <Label htmlFor="sshPort">Port</Label>
          <Input
            id="sshPort"
            type="number"
            min="1"
            max="65535"
            value={sshConfig.port}
            onChange={(e) => handleConfigChange("port", Number(e.target.value))}
            placeholder="22"
            className={errors.sshPort ? "border-red-500" : ""}
          />
          {errors.sshPort && <p className="text-sm text-red-500">{errors.sshPort}</p>}
        </div>
        <div>
          <Label htmlFor="sshUsername">Username</Label>
          <Input
            id="sshUsername"
            value={sshConfig.username}
            onChange={(e) => handleConfigChange("username", e.target.value)}
            placeholder="SSH username"
            className={errors.sshUsername ? "border-red-500" : ""}
          />
          {errors.sshUsername && <p className="text-sm text-red-500">{errors.sshUsername}</p>}
        </div>
        <div>
          <Label htmlFor="sshPassword">Password</Label>
          <Input
            id="sshPassword"
            type="password"
            value={sshConfig.enc_password || ""}
            onChange={(e) => handleConfigChange("enc_password", e.target.value)}
            placeholder="Password (or leave blank if using private key)"
            className={errors.sshPassword ? "border-red-500" : ""}
          />
          {errors.sshPassword && <p className="text-sm text-red-500">{errors.sshPassword}</p>}
        </div>
        <div>
          <Label htmlFor="sshPrivateKey">Private Key (optional)</Label>
          <Textarea
            id="sshPrivateKey"
            value={sshConfig.private_key || ""}
            onChange={(e) => handleConfigChange("private_key", e.target.value)}
            placeholder="Paste private key here..."
            className={errors.sshPrivateKey ? "border-red-500" : ""}
          />
          <input
            type="file"
            accept=".txt,.pem,.key,.pub,.crt"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                const text = await file.text();
                handleConfigChange("private_key", text);
              }
            }}
            className="mt-2"
          />
          {sshConfig.private_key && (
            <p className="text-xs text-green-600 mt-1">Private key loaded from file or pasted.</p>
          )}
          {errors.sshPrivateKey && <p className="text-sm text-red-500">{errors.sshPrivateKey}</p>}
        </div>
        <div>
          <Label>SSH Commands</Label>
          {sshConfig.commands.map((cmd, idx) => (
            <div key={idx} className="flex gap-2 items-center mb-2">
              <Input
                value={cmd.name}
                onChange={e => handleCommandChange(idx, "name", e.target.value)}
                placeholder="Command Name (e.g., Uptime Check)"
                className="w-1/3"
              />
              <Input
                value={cmd.command}
                onChange={e => handleCommandChange(idx, "command", e.target.value)}
                placeholder="Command (e.g., uptime)"
                className="w-1/2"
              />
              <Button type="button" size="icon" variant="ghost" onClick={() => removeCommand(idx)} disabled={sshConfig.commands.length === 1}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addCommand} className="mt-2">
            <Plus className="h-4 w-4 mr-1" /> Add Command
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AddSSH;
